import React from 'react';
import { useTranslation } from 'react-i18next';
import SectionHeading from './SectionHeading';
import { getLocaleList } from '../utils/localeList';
import useReveal from '../hooks/useReveal';

/**
 * Section 02 — the three tracks the hero promises: stablecoins, tokenization
 * and insured vaults / compliance.
 *
 * It sits directly under the hero because it is the hero, spelled out: the
 * headline sells those three words and this is the first place the visitor can
 * check that any of it is real. It exists because the headline started selling
 * them and nothing below it backed them up.
 *
 * Compliance rides inside this section rather than getting one of its own for
 * the same reason the use cases sit inside "Who we help": on its own it reads
 * as a disclaimer, next to the two tracks it qualifies it reads as the thing
 * that makes them shippable.
 *
 * Each track carries a concrete list rather than adjectives — the pattern the
 * competitors that convert best all use (DCM's "Products:", Etherfuse's "Used
 * for"). An abstract category only becomes a purchase once the buyer sees the
 * line item that matches their own problem.
 *
 * The compliance wording is deliberate and must not be loosened: we build the
 * controls, and the legal opinion comes from partner counsel. Claiming the
 * legal work itself would be a promise the company can't keep in front of a
 * regulated buyer.
 */
const StablecoinsAndTokenization = () => {
  const { t } = useTranslation();
  const reveal = useReveal();
  const tracks = getLocaleList(t, 'stablecoins.tracks');

  return (
    <div id="stablecoins" className="mainCont relative z-30 pt-5 mt-[100px] lg4:mt-[150px]" ref={reveal} data-reveal>
      <SectionHeading
        eyebrow={t('stablecoins.eyebrow')}
        title={t('stablecoins.title')}
        intro={t('stablecoins.intro')}
        image="/images/stablecoin-paltalabs-1.png"
      />

      <div className="grid grid-cols-1 md2:grid-cols-2 lg:grid-cols-3 gap-6">
        {tracks.map((track) => (
          <div
            key={track.name}
            className="landingCard p-6 flex flex-col"
          >
            <div className="font-grotesk text-[22px] font-bold text-main uppercase leading-[125%]">
              {track.name}
            </div>
            <p className="text-white text-sm mt-3">{track.description}</p>
            <ul className="mt-5 space-y-2 border-t border-white/10 pt-4">
              {track.items.map((item) => (
                <li key={item} className="flex items-start text-sm text-main-gray">
                  <span className="text-main mr-2">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StablecoinsAndTokenization;
